import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { cn } from '../../lib/utils';
import { Calendar, Clock, Coffee, Monitor, ChevronRight } from 'lucide-react';

export default function StudentTimetable() {
    const { user } = useAuth();
    const {
        mappings, subjects, faculty,
        timetableEntries, fetchTimetableForSection
    } = useData();

    const [loading, setLoading] = useState(true);
    const [studentData, setStudentData] = useState(null);
    const [slots, setSlots] = useState([]);

    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    useEffect(() => {
        if (!user?.id) return;

        fetch(`/api/my-profile?userId=${user.id}`)
            .then(res => res.json())
            .then(data => {
                const student = data.student;
                setStudentData(student);

                if (student && student.sectionId) {
                    fetchTimetableForSection(student.sectionId);

                    fetch('/api/sections')
                        .then(res => res.json())
                        .then(sections => {
                            const mySection = sections.find(s => s.id === student.sectionId);
                            if (mySection && mySection.timeConfigId) {
                                fetch(`/api/time-configs/${mySection.timeConfigId}/slots`)
                                    .then(res => res.json())
                                    .then(slotData => setSlots(slotData));
                            }
                        });
                }
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [user, fetchTimetableForSection]);


    const getMappingDetails = (mappingId) => {
        const mapping = mappings.find(m => m.id === mappingId);
        if (!mapping) return null;
        const subj = subjects.find(s => s.id === mapping.subjectId);
        const fac = faculty.find(f => f.id === mapping.facultyId);
        return { subject: subj?.name, code: subj?.code, faculty: fac?.name };
    };

    if (loading) return (
        <div className="min-h-[400px] flex flex-col items-center justify-center gap-4">
            <div className="h-10 w-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <p className="text-muted-foreground font-medium">Synchronizing Schedule...</p>
        </div>
    );

    if (!studentData) return (
        <div className="p-8 bg-destructive/5 rounded-2xl border border-destructive/20 text-center">
            <p className="text-destructive font-bold">Academic record synchronization failed.</p>
        </div>
    );

    if (slots.length === 0) return (
        <div className="p-12 text-center bg-card border border-border rounded-2xl">
            <div className="h-20 w-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar size={40} className="text-muted-foreground" />
            </div>
            <h3 className="text-xl font-bold mb-2">Schedule Not Released</h3>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">The timetable for your section hasn't been configured by the department head yet.</p>
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black font-heading text-foreground tracking-tight">My Timetable</h1>
                    <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                        <span className="font-bold text-primary">{studentData.branch}</span>
                        <span>•</span>
                        <span className="font-medium">{studentData.sectionName}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary/50 rounded-xl border border-border/50 text-xs font-bold uppercase tracking-wider text-muted-foreground md:mb-1">
                    <Calendar size={14} className="text-primary" />
                    Semester Academic Cycle
                </div>
            </div>

            {/* Scroll Indicator for Mobile */}
            <div className="lg:hidden flex items-center justify-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mb-2">
                <span>Swipe Horizontal</span>
                <ChevronRight size={12} className="animate-bounce-x" />
            </div>

            <div className="relative group">
                <div className="overflow-x-auto pb-6 -mx-4 px-4 md:mx-0 md:px-0">
                    <div className="min-w-[1100px] bg-card border border-border rounded-2xl overflow-hidden shadow-xl shadow-foreground/5">
                        {/* Header Row */}
                        <div className="flex bg-secondary/50 text-xs font-black text-muted-foreground uppercase tracking-widest divide-x divide-border border-b border-border">
                            <div className="w-32 p-4 flex-shrink-0 flex items-center justify-center bg-secondary/80 sticky left-0 z-10 font-heading">Day</div>
                            {slots.map(slot => (
                                <div
                                    key={slot.id}
                                    className={cn(
                                        "p-4 text-center flex-1 flex flex-col justify-center min-w-[140px]",
                                        slot.type === 'LAB' && "min-w-[240px] bg-primary/5",
                                        slot.type === 'BREAK' && "min-w-[90px] bg-orange-500/5"
                                    )}
                                >
                                    <span className="block text-foreground mb-1">{slot.name}</span>
                                    <span className="text-[10px] opacity-70 flex items-center justify-center gap-1.5 font-mono">
                                        <Clock size={10} className="text-primary" /> {slot.startTime} - {slot.endTime}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Rows */}
                        <div className="divide-y divide-border">
                            {days.map(day => (
                                <div key={day} className="flex divide-x divide-border hover:bg-muted/5 transition-all duration-300 group/row">
                                    <div className="w-32 p-4 font-black text-xs flex items-center justify-center bg-secondary/30 flex-shrink-0 sticky left-0 z-10 transition-colors group-hover/row:bg-primary/5 font-heading">
                                        {day}
                                    </div>
                                    {slots.map(slot => {
                                        const key = `${day}-${slot.id}`;
                                        const mappingData = timetableEntries[key];
                                        const details = mappingData ? getMappingDetails(mappingData.mappingId) : null;

                                        if (slot.type === 'BREAK') {
                                            return (
                                                <div key={key} className="flex-1 min-w-[90px] bg-orange-500/5 flex items-center justify-center group-hover/row:bg-orange-500/10 transition-colors">
                                                    <Coffee size={20} className="text-orange-500/30" />
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={key}
                                                className={cn(
                                                    "p-3 flex-1 min-w-[140px] min-h-[140px] flex flex-col justify-center items-center text-center gap-2 group-hover/row:bg-accent/5 transition-all duration-300",
                                                    slot.type === 'LAB' && "min-w-[240px]",
                                                    details ? "bg-primary/[0.02]" : "bg-transparent"
                                                )}
                                            >
                                                {details ? (
                                                    <div className="animate-in fade-in slide-in-from-bottom-1 duration-500 space-y-2">
                                                        <span className="inline-block px-2 py-0.5 rounded-md bg-primary text-[10px] font-black text-white shadow-sm shadow-primary/30 uppercase">
                                                            {details.code}
                                                        </span>
                                                        <h4 className="text-xs font-bold text-foreground leading-snug line-clamp-2 max-w-[120px] mx-auto">
                                                            {details.subject}
                                                        </h4>
                                                        <div className="flex items-center gap-1.5 mt-2 justify-center text-[10px] font-bold text-muted-foreground bg-secondary/80 px-2 py-1 rounded-lg border border-border/50">
                                                            <div className="h-4 w-4 rounded-full bg-primary/20 flex items-center justify-center text-primary overflow-hidden">
                                                                {details.faculty[0]}
                                                            </div>
                                                            {details.faculty}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs font-black text-muted-foreground/10 tracking-widest">- FREE -</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
